import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Tooltip,
  Fade,
  Stack,
  IconButton
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ContentCopy as CopyIcon,
  Replay as ReplayIcon,
  ThumbUpOutlined as ThumbsUpIcon,
  ThumbDownOutlined as ThumbsDownIcon,
  MenuBook as BookIcon
} from "@mui/icons-material";
import MessageBubble from "./MessageBubble";
import MessageRenderer from "./MessageRenderer";
import SourcesPanel from "./SourcesPanel";
import TypingIndicator from "./TypingIndicator";
import ErrorMessage from "./ErrorMessage";
import SuggestedQuestions from "./SuggestedQuestions";
import EmptyState from "./EmptyState";
import ChatInput from "./ChatInput";
import AssistantHeader from "./AssistantHeader";

const ChatWindow = ({
  messages,
  loading,
  error,
  onSendMessage,
  onRetry,
  onCopy,
  onRegenerate,
  onFeedback,
  suggestedQuestions,
  onSelectSuggestedQuestion,
  onNewChat,
  onClearConversation,
  onToggleSources,
  showSources,
  onStopGeneration,
}) => {
  const theme = useTheme();
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const handleFeedback = (messageId, isPositive) => {
    if (onFeedback) {
      onFeedback(messageId, isPositive);
    }
  };

  const lastAssistantMessage = messages.filter(m => m.role === "assistant").pop();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'grey.50' }}>
      {/* Header */}
      <AssistantHeader
        status={loading ? "responding" : "available"}
        onNewChat={onNewChat}
        onClearConversation={onClearConversation}
        onToggleSources={onToggleSources}
        showSourcesButton={!!lastAssistantMessage?.sources}
      />

      {/* Messages Area */}
      <Box className="assistant-chat-content" sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 4 }, position: 'relative' }}>
        <Stack spacing={4} className="assistant-content-inner" sx={{ maxWidth: '1000px', mx: 'auto', pb: 4, px: { xs: 0, md: 2 } }}>
          {messages.length === 0 && !loading && !error && (
            <EmptyState
              suggestedQuestions={suggestedQuestions}
              onSelectQuestion={onSelectSuggestedQuestion}
            />
          )}

          {messages.map((message, index) => (
            <Fade in={true} timeout={500} key={index}>
              <Box>
                <MessageBubble
                  role={message.role}
                  content={message.content}
                  timestamp={message.timestamp}
                  intent={message.intent}
                />
                
                {message.role === "assistant" && (
                  <Box sx={{ ml: 7, mt: 1 }}>
                    {/* Sources (if Context Panel is not used or handled here) */}
                    {message.sources && !showSources && (
                      <Box sx={{ mb: 1 }}>
                        <Button
                          size="small"
                          startIcon={<BookIcon />}
                          onClick={onToggleSources}
                          sx={{ color: 'text.secondary', textTransform: 'none', '&:hover': { color: 'primary.main', bgcolor: 'transparent' } }}
                        >
                          Afficher les sources
                        </Button>
                      </Box>
                    )}

                    {/* Response time */}
                    {message.responseTime && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                        Réponse en {message.responseTime}ms
                      </Typography>
                    )}

                    {/* Actions */}
                    <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        sx={{
                          mt: 1,
                          flexWrap: "wrap",
                        }}
                      >
                      <Tooltip title="Copier">
                        <Button
                          size="small"
                          startIcon={<CopyIcon fontSize="small" />}
                          onClick={() => onCopy(typeof message.content === 'string' ? message.content : '')}
                          sx={{ color: 'text.secondary', textTransform: 'none', px: 1, minWidth: 0, '&:hover': { bgcolor: 'grey.100', color: 'text.primary' } }}
                        >
                          Copier
                        </Button>
                      </Tooltip>
                      <Tooltip title="Relancer">
                        <Button
                          size="small"
                          startIcon={<ReplayIcon fontSize="small" />}
                          onClick={() => onRegenerate(message)}
                          sx={{ color: 'text.secondary', textTransform: 'none', px: 1, minWidth: 0, '&:hover': { bgcolor: 'grey.100', color: 'text.primary' } }}
                        >
                          Relancer
                        </Button>
                      </Tooltip>
                      {onFeedback && (
                        <>
                          <Tooltip title="Utile">
                            <Button
                              size="small"
                              startIcon={<ThumbsUpIcon fontSize="small" />}
                              onClick={() => handleFeedback(index, true)}
                              sx={{ color: 'text.secondary', textTransform: 'none', px: 1, minWidth: 0, '&:hover': { bgcolor: 'success.50', color: 'success.main' } }}
                            >
                              Utile
                            </Button>
                          </Tooltip>
                          <Tooltip title="Pas utile">
                            <Button
                              size="small"
                              startIcon={<ThumbsDownIcon fontSize="small" />}
                              onClick={() => handleFeedback(index, false)}
                              sx={{ color: 'text.secondary', textTransform: 'none', px: 1, minWidth: 0, '&:hover': { bgcolor: 'error.50', color: 'error.main' } }}
                            >
                              Pas utile
                            </Button>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Fade>
          ))}

          {loading && (
            <Box sx={{ mt: 2 }}>
              <TypingIndicator />
            </Box>
          )}

          {error && (
            <Box sx={{ mt: 2 }}>
              <ErrorMessage error={error} onRetry={onRetry} />
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Stack>
      </Box>

      {/* Input Area */}
      <ChatInput
        onSendMessage={onSendMessage}
        loading={loading}
        onStopGeneration={onStopGeneration}
      />
    </Box>
  );
};

export default ChatWindow;
